
"use client";
import React, { useId, Suspense, useMemo } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useParticles } from "./ParticlesContext";
import { createSparklesOptions } from "./config/sparkleOptions";
import { useParticlesLoaded } from "./hooks/useParticlesLoaded";

const Particles = React.lazy(() =>
  import("@tsparticles/react").then((module) => ({
    default: module.Particles,
  }))
);

export type SparklesCoreProps = {
  id?: string;
  className?: string;
  background?: string;
  particleSize?: number;
  minSize?: number;
  maxSize?: number;
  speed?: number;
  particleColor?: string;
  particleDensity?: number;
  debug?: boolean;
};

export const SparklesCore = (props: SparklesCoreProps) => {
  const {
    id,
    className,
    background,
    minSize,
    maxSize,
    speed,
    particleColor,
    particleDensity,
    debug,
  } = props;
  
  const { isInitialized, isLoading, error, usingFallback } = useParticles();
  const { controls, particlesLoaded } = useParticlesLoaded();
  const generatedId = useId();
  const componentId = id || generatedId;
  

  // Create options based on props
  const options = useMemo(
    () =>
      createSparklesOptions({
        background,
        minSize,
        maxSize,
        speed,
        particleColor,
        particleDensity,
      }),
    [background, minSize, maxSize, speed, particleColor, particleDensity]
  );

  // When still loading, return an empty div
  if (isLoading) {
    return <div className={cn("relative", className)} />;
  }

  // Render a fallback if particles aren't available
  if (usingFallback || error || !isInitialized) {
    return (
      <div 
        className={cn("relative overflow-hidden", className)} 
        style={{ background: background || "transparent" }}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-foreground/5 to-transparent opacity-30" />
        
        {(usingFallback || !isInitialized) && (
          <>
            <div className="absolute top-1/4 left-1/2 h-1 w-1 rounded-full bg-white/20 animate-pulse" />
            <div className="absolute top-3/4 left-1/4 h-0.5 w-0.5 rounded-full bg-white/30 animate-pulse" />
            <div className="absolute top-1/3 left-3/4 h-0.5 w-0.5 rounded-full bg-white/25 animate-pulse" />
            <div className="absolute top-2/3 left-1/3 h-1 w-1 rounded-full bg-white/15 animate-pulse" />
            <div className="absolute top-1/5 left-1/5 h-0.5 w-0.5 rounded-full bg-white/20 animate-pulse" />
          </>
        )}
        
        {error && debug && (
          <div className="absolute bottom-0 left-0 right-0 bg-red-500/20 text-white text-xs p-1">
            Error: {error.message}
          </div>
        )}
      </div>
    );
  }

  return (
    <motion.div 
      animate={controls} 
      initial={{ opacity: 0 }}
      className={cn("relative", className)}
    >
      <Suspense fallback={<div className={cn("h-full w-full", className)} />}>
        <Particles
          id={componentId}
          className={cn("h-full w-full")}
          particlesLoaded={particlesLoaded}
          options={options}
        />
      </Suspense>
    </motion.div>
  );
};
